/**
 * Banner Component - Controller
 * ==============================
 *
 * Controlador del componente Banner.
 * Maneja la lógica del banner promocional.
 */

import type { IScope, IAugmentedJQuery, ITranscludeFunction } from 'angular';
import { hasTranscludedContent } from '../../_base/transclusion';
import {
  VARIANTS,
  SIZES,
  ICONS,
  BASE_CLASSES,
  DEFAULTS,
  BannerVariant,
  BannerSize,
  BannerIconType,
  IconPosition,
  VariantStyles,
  SizeStyles
} from './constants';

// ============================================================================
// TYPES
// ============================================================================

/**
 * Interfaz para los bindings del componente
 */
export interface BannerBindings {
  // Contenido
  title?: string;
  description?: string;
  buttonText?: string;

  // Icono
  iconType?: BannerIconType;
  customIcon?: string;
  iconPosition?: IconPosition;
  showIcon?: boolean;

  // Apariencia
  variant?: BannerVariant;
  size?: BannerSize;

  // Comportamiento
  dismissible?: boolean;
  showButton?: boolean;

  // Acciones
  onButtonClick?: (args: { $event: Event }) => void;
  onDismiss?: (args: { $event: Event }) => void;
  onClick?: (args: { $event: Event }) => void;
}

/**
 * Interfaz para cambios en bindings
 */
interface BindingChanges {
  variant?: { currentValue: BannerVariant; isFirstChange: () => boolean };
  size?: { currentValue: BannerSize; isFirstChange: () => boolean };
  [key: string]: unknown;
}

// ============================================================================
// CONTROLLER
// ============================================================================

/**
 * Clase controlador del Banner
 */
export class BannerController implements BannerBindings {
  static $inject = ['$scope', '$element', '$sce', '$transclude'];

  // Propiedades inyectadas
  private $scope: IScope | null;
  private $element: IAugmentedJQuery | null;
  private $sce: angular.ISCEService | null;
  private $transclude: ITranscludeFunction | null;

  // Bindings
  title?: string;
  description?: string;
  buttonText?: string;
  iconType?: BannerIconType;
  customIcon?: string;
  iconPosition?: IconPosition;
  showIcon?: boolean;
  variant?: BannerVariant;
  size?: BannerSize;
  dismissible?: boolean;
  showButton?: boolean;
  onButtonClick?: (args: { $event: Event }) => void;
  onDismiss?: (args: { $event: Event }) => void;
  onClick?: (args: { $event: Event }) => void;

  // Estado interno
  isVisible: boolean = true;
  trustedIconHtml: unknown = null;
  hasTransclude: boolean = false;

  constructor(
    $scope: IScope,
    $element: IAugmentedJQuery,
    $sce: angular.ISCEService,
    $transclude: ITranscludeFunction
  ) {
    this.$scope = $scope;
    this.$element = $element;
    this.$sce = $sce;
    this.$transclude = $transclude;

    // Bind de métodos
    this.handleButtonClick = this.handleButtonClick.bind(this);
    this.handleDismiss = this.handleDismiss.bind(this);
    this.handleClick = this.handleClick.bind(this);
  }

  // =========================================================================
  // LIFECYCLE HOOKS
  // =========================================================================

  $onInit(): void {
    this._applyDefaults();
    this._updateTrustedIcon();
    this._checkTransclude();
  }

  $onChanges(changes: BindingChanges): void {
    if (changes.variant && !changes.variant.isFirstChange()) {
      this._validateVariant();
    }
    if (changes.size && !changes.size.isFirstChange()) {
      this._validateSize();
    }
    if (changes.iconType || changes.customIcon) {
      this._updateTrustedIcon();
    }
  }

  $onDestroy(): void {
    this.$scope = null;
    this.$element = null;
    this.$sce = null;
    this.$transclude = null;
  }

  // =========================================================================
  // MÉTODOS PRIVADOS
  // =========================================================================

  private _applyDefaults(): void {
    this.variant = this.variant || DEFAULTS.variant;
    this.size = this.size || DEFAULTS.size;
    this.iconType = this.iconType || DEFAULTS.iconType;
    this.iconPosition = this.iconPosition || DEFAULTS.iconPosition;
    this.showIcon = this.showIcon !== false;
    this.showButton = this.showButton !== false;
    this.dismissible = this.dismissible || DEFAULTS.dismissible;
  }

  private _validateVariant(): void {
    if (this.variant && !VARIANTS[this.variant]) {
      console.warn(`[Banner] Variant "${this.variant}" no válido. Usando "${DEFAULTS.variant}".`);
      this.variant = DEFAULTS.variant;
    }
  }

  private _validateSize(): void {
    if (this.size && !SIZES[this.size]) {
      console.warn(`[Banner] Size "${this.size}" no válido. Usando "${DEFAULTS.size}".`);
      this.size = DEFAULTS.size;
    }
  }

  private _updateTrustedIcon(): void {
    if (this.$sce) {
      const iconSvg = this.customIcon || ICONS[this.iconType || DEFAULTS.iconType] || '';
      this.trustedIconHtml = this.$sce.trustAsHtml(iconSvg);
    }
  }

  /**
   * Verificar si hay contenido transcluido (modo compound).
   *
   * `isSlotFilled('')` NO sirve acá: con `transclude: true` booleano (como
   * este componente) $$slots solo se puebla en la forma multi-slot, así que
   * siempre devolvería false. Se usa `hasTranscludedContent`, que sondea el
   * contenido real con un clone descartable.
   */
  private _checkTransclude(): void {
    this.hasTransclude = hasTranscludedContent(this.$transclude);
  }

  // =========================================================================
  // MÉTODOS PÚBLICOS - CLASES CSS
  // =========================================================================

  getContainerClasses(): string[] {
    const sizeStyles = this.getSizeClasses();
    const variantStyles = this.getVariantClasses();

    const baseClass = this.iconPosition === 'top'
      ? BASE_CLASSES.containerVertical
      : BASE_CLASSES.container;

    return [
      baseClass,
      sizeStyles.container,
      sizeStyles.gap,
      variantStyles.container
    ];
  }

  getVariantClasses(): VariantStyles {
    return VARIANTS[this.variant || DEFAULTS.variant] || VARIANTS[DEFAULTS.variant];
  }

  getSizeClasses(): SizeStyles {
    return SIZES[this.size || DEFAULTS.size] || SIZES[DEFAULTS.size];
  }

  getIconWrapperClasses(): string[] {
    const sizeStyles = this.getSizeClasses();
    const variantStyles = this.getVariantClasses();

    return [
      BASE_CLASSES.iconWrapper,
      sizeStyles.iconWrapper,
      variantStyles.iconBg
    ];
  }

  getIconClasses(): string[] {
    const sizeStyles = this.getSizeClasses();
    const variantStyles = this.getVariantClasses();

    return [
      sizeStyles.icon,
      variantStyles.iconColor
    ];
  }

  getTitleClasses(): string[] {
    const sizeStyles = this.getSizeClasses();
    const variantStyles = this.getVariantClasses();

    return [
      sizeStyles.title,
      variantStyles.title
    ];
  }

  getDescriptionClasses(): string[] {
    const sizeStyles = this.getSizeClasses();
    const variantStyles = this.getVariantClasses();

    return [
      sizeStyles.description,
      variantStyles.description,
      'ec-mt-1'
    ];
  }

  getButtonClasses(): string[] {
    const sizeStyles = this.getSizeClasses();
    const variantStyles = this.getVariantClasses();

    return [
      BASE_CLASSES.button,
      sizeStyles.button,
      variantStyles.button,
      variantStyles.buttonHover
    ];
  }

  getDismissClasses(): string[] {
    const variantStyles = this.getVariantClasses();
    return [
      'ec-ml-2 ec-p-1 ec-rounded-full ec-transition-colors ec-cursor-pointer',
      variantStyles.iconColor,
      'hover:ec-bg-black/10'
    ];
  }

  // =========================================================================
  // MÉTODOS PÚBLICOS - EVENTOS
  // =========================================================================

  handleButtonClick($event: Event): void {
    $event.stopPropagation();

    if (typeof this.onButtonClick === 'function') {
      this.onButtonClick({ $event });
    }
  }

  handleDismiss($event: Event): void {
    $event.stopPropagation();
    this.isVisible = false;

    if (typeof this.onDismiss === 'function') {
      this.onDismiss({ $event });
    }
  }

  handleClick($event: Event): void {
    if (typeof this.onClick === 'function') {
      this.onClick({ $event });
    }
  }

  // =========================================================================
  // GETTERS
  // =========================================================================

  get hasButton(): boolean {
    return this.showButton !== false && !!this.buttonText;
  }

  get hasDescription(): boolean {
    return !!this.description;
  }

  get isHorizontal(): boolean {
    return this.iconPosition !== 'top';
  }

  /**
   * Modo compound: transclusión cruda en el área de contenido,
   * sin los bindings title/description. Opt-in — si se usan title/description
   * el comportamiento legacy queda intacto.
   */
  get isCompound(): boolean {
    return this.hasTransclude && !this.title && !this.description;
  }
}

export default BannerController;

// ============================================================================
// CONTROLLERS DE HIJOS (Compound Components)
// ============================================================================

/**
 * Hijo compound: título del banner, variant/size-aware.
 * Puede vivir dentro de ecBanner o ecBannerVertical — ambos comparten
 * BannerController, por eso el require dual opcional (solo uno de los dos
 * quedará definido según el padre real). En uso standalone (sin padre) cae
 * a un peso de fuente estático razonable.
 */
export class BannerTitleController {
  static $inject = [];
  banner?: BannerController; // require ^?ecBanner
  bannerVertical?: BannerController; // require ^?ecBannerVertical

  private get ctrl(): BannerController | undefined {
    return this.banner || this.bannerVertical;
  }

  getClasses(): string[] {
    return this.ctrl ? this.ctrl.getTitleClasses() : ['ec-font-semibold'];
  }
}

/**
 * Hijo compound: texto/descripción del banner, variant/size-aware.
 * Mismo require dual opcional que ecBannerTitle.
 */
export class BannerTextController {
  static $inject = [];
  banner?: BannerController; // require ^?ecBanner
  bannerVertical?: BannerController; // require ^?ecBannerVertical

  private get ctrl(): BannerController | undefined {
    return this.banner || this.bannerVertical;
  }

  getClasses(): string[] {
    return this.ctrl ? this.ctrl.getDescriptionClasses() : [];
  }
}
